/**
 * Four hand-rolled chart primitives.
 *
 * The dashboard needs exactly these shapes, all part-to-whole or ranked comparisons.
 * A charting library would add ~100 KB for that, and every colour here is chosen by
 * MEANING (tag identity, progress state) rather than by series index, which a generic
 * palette would fight.
 */

/** A single part-to-whole bar. Reads faster than a donut for a 3-4 state breakdown. */
export function StackedBar({ segments, total, showLabels = true }) {
  const sum = total ?? segments.reduce((n, s) => n + s.value, 0);
  if (!sum) return <div className="stacked-bar" aria-hidden="true" />;

  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <div
        className="stacked-bar"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${s.value}`).join(', ')}
      >
        {segments
          .filter((s) => s.value > 0)
          .map((segment) => {
            const pct = (segment.value / sum) * 100;
            return (
              <span
                key={segment.label}
                style={{ flex: `0 0 ${pct}%`, background: segment.color, color: segment.fg || '#fff' }}
                title={`${segment.label}: ${segment.value} (${pct.toFixed(1)}%)`}
              >
                {pct > 7 ? segment.value : ''}
              </span>
            );
          })}
      </div>
      {showLabels && (
        <div className="stacked-legend">
          {segments.map((segment) => (
            <span key={segment.label}>
              <i className="swatch" style={{ background: segment.color }} />
              {segment.label} <strong>{segment.value}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Horizontal ranked bars - the labels are long, so vertical bars would not fit. */
export function BarList({ items, max }) {
  const ceiling = max ?? Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="bar-list">
      {items.map((item) => (
        <div className="bar-row" key={item.label}>
          <span className="bar-label">{item.label}</span>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{
                width: `${(item.value / ceiling) * 100}%`,
                background: item.color || 'var(--accent)',
              }}
            />
          </div>
          <span className="bar-value">
            {item.value}
            {item.suffix ? ` ${item.suffix}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

/** One ratio, big number in the middle - the only place a donut earns its keep. */
export function DonutStat({ value, total, label, color = 'var(--accent)', size = 108 }) {
  const pct = total > 0 ? value / total : 0;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="row gap-4">
      <svg width={size} height={size} role="img" aria-label={`${label}: ${value} of ${total}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border)"
          strokeWidth="10"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${circumference * pct} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 20, fontWeight: 700, fill: 'var(--fg)' }}
        >
          {Math.round(pct * 100)}%
        </text>
      </svg>
      <div>
        <div style={{ fontWeight: 650 }}>{label}</div>
        <div className="small muted">
          {value} of {total} done
        </div>
      </div>
    </div>
  );
}

export const TAG_COLORS = {
  hallucinated: 'var(--tag-hallucinated-fg)',
  uncertain: 'var(--tag-uncertain-fg)',
  wrong_uncertainty: 'var(--tag-wrong-fg)',
};

export const PROGRESS_COLORS = {
  done: 'var(--orange-700)',
  in_progress: 'var(--orange-400)',
  assigned: 'var(--orange-200)',
  unassigned: 'var(--border)',
};
